import React, { useState } from "react";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import PhoneInputWrapper from "./utils/clientPhoneInputWrapper";
import { validateContactForm } from "./validators/clientContactFormValidator";

const Contact = () => {
  const apiBaseUrl = process.env.REACT_APP_API_URL;

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    subject: "",
    phone: "",
    countryName: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [formStatus, setFormStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormStatus(null);
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePhoneChange = (value, country) => {
    setFormStatus(null);
    setFormData((prev) => ({
      ...prev,
      phone: value,
      countryName: country.name,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus(null);
    const validationErrors = validateContactForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});

    if (!apiBaseUrl) {
      console.error("REACT_APP_API_URL is not configured");
      setFormStatus({
        type: "error",
        text: "Inquiry could not be sent. Please try again or contact SUPPoland directly.",
      });
      return;
    }

    const normalizedApiBaseUrl = apiBaseUrl.replace(/\/$/, "");

    try {
      const response = await fetch(`${normalizedApiBaseUrl}/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setFormStatus({
          type: "success",
          text: "Project inquiry sent successfully. SUPPoland will review your message and respond as soon as possible.",
        });
        setFormData({
          name: "",
          email: "",
          company: "",
          subject: "",
          phone: "",
          countryName: "",
          message: "",
        });
      } else {
        setFormStatus({
          type: "error",
          text: "Inquiry could not be sent. Please try again or contact SUPPoland directly.",
        });
      }
    } catch (error) {
      console.error("Error:", error);
      setFormStatus({
        type: "error",
        text: "Inquiry could not be sent. Please try again or contact SUPPoland directly.",
      });
    }
  };

  return (
    <section id="contact" className="section-spacing contact-section">
      <Container>
        <Row className="g-5">
          <Col lg={5}>
            <div className="contact-intro-panel">
              <p className="section-kicker">Contact / Project Inquiry</p>
              <h2 className="section-title">Send a Project Inquiry</h2>
              <p className="lead section-lead">
                Need rope access or work-at-height support for an industrial,
                construction, or technical project?
              </p>
              <p>
                Send the project location, expected scope of work, preferred
                timeframe, and cooperation details. SUPPoland will review your
                inquiry and respond regarding possible availability and next
                steps.
              </p>
              <div className="contact-details">
                <p>
                  <a className="contact-link" href="tel:+48797513494">
                    +48 797 513 494
                  </a>
                </p>
                <p>
                  <a className="contact-link" href="mailto:info@suppoland.pl">
                    info@suppoland.pl
                  </a>
                </p>
              </div>
            </div>
          </Col>
          <Col lg={7}>
            <Form onSubmit={handleSubmit} className="contact-form inquiry-form" noValidate>
              <p className="small form-note mb-3">Required fields are marked with <span className="required-marker">*</span>.</p>
              <Form.Group controlId="formName" className="form-field-group">
                <Form.Label>Name <span className="required-marker">*</span></Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter your name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  isInvalid={Boolean(errors.name)}
                  required
                />
                {errors.name && (
                  <div className="text-danger">{errors.name}</div>
                )}
              </Form.Group>

              <Form.Group controlId="formEmail" className="form-field-group">
                <Form.Label>Email address <span className="required-marker">*</span></Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Enter email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  isInvalid={Boolean(errors.email)}
                  required
                />
                {errors.email && (
                  <div className="text-danger">{errors.email}</div>
                )}
              </Form.Group>

              <Form.Group controlId="formSubject" className="form-field-group">
                <Form.Label>Inquiry subject <span className="required-marker">*</span></Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. Rope access support request"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  isInvalid={Boolean(errors.subject)}
                  maxLength={120}
                  required
                />
                {errors.subject && (
                  <div className="text-danger">{errors.subject}</div>
                )}
              </Form.Group>

              <Form.Group controlId="formCompany" className="form-field-group">
                <Form.Label>Company name (optional)</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter company name"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  isInvalid={Boolean(errors.company)}
                />
                {errors.company && (
                  <div className="text-danger">{errors.company}</div>
                )}
              </Form.Group>

              <Form.Group controlId="formPhone" className="form-field-group">
                <Form.Label>Phone number (optional)</Form.Label>
                <PhoneInputWrapper
                  country={"pl"}
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  inputClass={errors.phone ? "is-invalid" : ""}
                  inputProps={{
                    name: "phone",
                    required: false,
                  }}
                />
                {errors.phone && (
                  <div className="text-danger">{errors.phone}</div>
                )}
              </Form.Group>

              <Form.Group controlId="formMessage" className="form-field-group">
                <Form.Label>Project message <span className="required-marker">*</span></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  placeholder="Briefly describe the project, site conditions and required work-at-height support."
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  isInvalid={Boolean(errors.message)}
                  required
                />
                {errors.message && (
                  <div className="text-danger">{errors.message}</div>
                )}
              </Form.Group>

              <Button type="submit" className="btn-cta-primary inquiry-submit">
                Send Project Inquiry
              </Button>
              {formStatus && (
                <div
                  className={`form-status form-status-${formStatus.type}`}
                  role={formStatus.type === "error" ? "alert" : "status"}
                  aria-live="polite"
                >
                  {formStatus.text}
                </div>
              )}
              <p className="small form-note mt-3 mb-0">
                The information you provide will be used only to respond to your inquiry.
              </p>
            </Form>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default Contact;